/**
 * BankStatementWorkflow.jsx  —  VATICA V5
 *
 * Top-level Bank Statements tool rendered inside ClientDashboard.
 * Props:
 *   bankSub          {string}   "current" | "loan"
 *   onSubTabChange   {fn}
 *   clientId         {number|null}  pre-selected client (when launched from ClientProfile)
 *   clientName       {string}
 */

import { useState, useRef, useEffect } from "react";
import {
  extractBankStatement,
  downloadBankStatementExcel,
  injectAndDownload,
  getMyBankStatements,
  deleteMyBankStatement,
  getMyClients,
  bankStatementFilename,
} from "./api";
import ConfirmModal from "./ConfirmModal";
import { useProcessingGuard } from "./useProcessingGuard";
import { useBankStateGuard } from "./useBankStateGuard";
import MergeStatementsWorkflow from "./MergeStatementsWorkflow";

// ── Bank definitions ──────────────────────────────────────────────────────────
const CURRENT_BANKS = [
  { slug: "standard_bank",   label: "Standard Bank",      icon: "🏦" },
  { slug: "fnb",             label: "FNB Eswatini",       icon: "🏛" },
  { slug: "eswatini_bank",   label: "Eswatini Bank",      icon: "🏢" },
  { slug: "nedbank_current", label: "Nedbank (Current)",    icon: "🏦" },
];
const LOAN_BANKS = [
  { slug: "standard_bank_loan", label: "Standard Bank (Finance Lease)",          icon: "📋" },
  { slug: "nedbank",            label: "Nedbank (Loan)",                          icon: "📋" },
  { slug: "sbs_loan",           label: "Swaziland Building Society (Loan)",       icon: "🏠" },
];

const BANK_SUB_TABS = [
  { id: "current", icon: "💳", label: "Current Account" },
  { id: "loan",    icon: "📋", label: "Loan Account"    },
];

export default function BankStatementWorkflow({
  bankSub        = "current",
  onSubTabChange = () => {},
  clientId: propClientId = null,
  clientName: propClientName = "",
}) {
  // ── State ──────────────────────────────────────────────────────────────────
  const { setProcessing: setBankProcessing } = useProcessingGuard("bank");

  // ── Per-tab volatile state ───────────────────────────────────────────────
  // PROBLEM THIS FIXES: BankStatementWorkflow is mounted ONCE (PersistentRoute
  // keeps it alive) and just re-rendered with a different `bankSub` prop when
  // the user clicks Current ↔ Loan. Plain useState for selectedBank, pdfFile,
  // extracting, progress, stage, logs, apiError, injecting, invoicesFile was
  // therefore a SINGLE value shared by both sub-tabs — starting an extraction
  // on Current flipped `extracting` to true for Loan too, so switching tabs
  // showed someone else's progress bar / result and blocked starting a second,
  // independent extraction on the other tab.
  //
  // Fix: keep all of that state in one object keyed by tab ("current"/"loan"),
  // each tab gets its own independent slot, and async handlers always write
  // back to the tab they were started on (captured as `tabKey` at call time)
  // rather than "whichever tab happens to be active when the request finishes".
  const makeTabState = () => ({
    selectedBank: null,
    pdfFile: null,
    dragOver: false,
    extracting: false,
    progress: 0,
    stage: "",
    logs: [],
    apiError: "",
    injecting: false,
    invoicesFile: null,
  });
  const [tabStates, setTabStates] = useState(() => ({
    current: makeTabState(),
    loan:    makeTabState(),
  }));

  // Merge a partial update into a SPECIFIC tab's state. Accepts either a
  // plain object or an updater function (prevTabState) => partialPatch, the
  // latter needed when the patch depends on existing state (e.g. logs).
  const updateTab = (tabKey, patch) => {
    setTabStates(prev => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        ...(typeof patch === "function" ? patch(prev[tabKey]) : patch),
      },
    }));
  };

  // Shorthand for the ACTIVE tab — drives everything in the render below.
  const ts = tabStates[bankSub];
  const setTs = patch => updateTab(bankSub, patch);

  // extracted is managed by useBankStateGuard — stored outside React lifecycle
  // so sub-tab switches and page navigation can never wipe a completed result.
  // saveResultToTab/clearResultForTab let async handlers target an explicit
  // tab even after the user has navigated away from it mid-extraction.
  const { result: extracted, saveResult, clearResult, saveResultToTab, clearResultForTab } =
    useBankStateGuard(bankSub);
  const [stmtLists,     setStmtLists]     = useState({ current: [], loan: [] });
  const [listLoadingMap, setListLoadingMap] = useState({ current: false, loan: false });
  const stmtList    = stmtLists[bankSub];
  const listLoading = listLoadingMap[bankSub];

  const [confirmModal, setConfirmModal] = useState(null);

  // client selector
  const [clientList,    setClientList]   = useState([]);
  const [selectedClient, setSelectedClient] = useState(
    propClientId ? { id: propClientId, name: propClientName } : null
  );

  const pdfRef      = useRef(null);
  const invRef      = useRef(null);
  const logRef      = useRef(null);

  // Banks for current sub-tab
  const banks = bankSub === "current" ? CURRENT_BANKS : LOAN_BANKS;

  // Per-tab state means there is no longer anything to reset when bankSub
  // changes — each tab's selectedBank/pdfFile/logs/etc. simply persist in its
  // own slot in `tabStates` and useBankStateGuard restores the right
  // `extracted` result automatically. (Previously this effect wiped shared
  // state on every sub-tab click, which is what caused Current's in-progress
  // extraction to visually leak into the Loan tab.)

  // Ref counter so the sidebar "extraction in progress" indicator (driven by
  // useProcessingGuard's single "bank" key) stays ON for as long as EITHER
  // sub-tab has a request in flight, and only turns off once BOTH are done —
  // a plain true/false toggle would have the first tab to finish wrongly
  // clear the indicator while the other tab is still extracting.
  const activeRequestsRef = useRef(0);
  function beginBankProcessing() {
    activeRequestsRef.current += 1;
    setBankProcessing(true);
  }
  function endBankProcessing() {
    activeRequestsRef.current = Math.max(0, activeRequestsRef.current - 1);
    if (activeRequestsRef.current === 0) setBankProcessing(false);
  }

  // Load clients & statement history on mount / when the visible tab changes
  useEffect(() => {
    getMyClients().then(d => setClientList(d || [])).catch(() => {});
    loadStatements();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankSub]);

  async function loadStatements(tabKey = bankSub) {
    setListLoadingMap(prev => ({ ...prev, [tabKey]: true }));
    try {
      const all = await getMyBankStatements(propClientId || null);
      const accountType = tabKey === "current" ? "current" : "loan";
      const filtered = (all || []).filter(s => s.account_type === accountType);
      setStmtLists(prev => ({ ...prev, [tabKey]: filtered }));
    } catch (e) {
      console.error(e);
    } finally {
      setListLoadingMap(prev => ({ ...prev, [tabKey]: false }));
    }
  }

  // Tracks whichever tab is actually on-screen right now, independent of any
  // stale closures inside in-flight async handlers — used only to decide
  // whether a log scroll-into-view should fire (no point scrolling a log box
  // that isn't currently rendered).
  const bankSubRef = useRef(bankSub);
  useEffect(() => { bankSubRef.current = bankSub; }, [bankSub]);

  function addLog(tabKey, msg, type = "") {
    updateTab(tabKey, prevTab => ({ logs: [...prevTab.logs, { msg, type }] }));
    setTimeout(() => {
      if (logRef.current && tabKey === bankSubRef.current) {
        logRef.current.scrollTop = logRef.current.scrollHeight;
      }
    }, 40);
  }

  // ── File drop / select ──────────────────────────────────────────────────────
  function handleDrop(e) {
    e.preventDefault();
    setTs({ dragOver: false });
    const f = e.dataTransfer.files[0];
    if (f && f.type === "application/pdf") setTs({ pdfFile: f });
  }

  function handlePdfSelect(e) {
    const f = e.target.files?.[0];
    if (f) setTs({ pdfFile: f });
  }

  // ── Extraction ──────────────────────────────────────────────────────────────
  async function handleExtract() {
    // Fixed for the lifetime of this call — this is what makes a Current
    // extraction stay a Current extraction even if the user switches to
    // Loan (or back) while it's running.
    const tabKey = bankSub;
    const file   = ts.pdfFile;
    const bank   = ts.selectedBank;
    if (!file || !bank) return;

    updateTab(tabKey, { extracting: true, apiError: "", invoicesFile: null, logs: [] });
    beginBankProcessing();
    clearResultForTab(tabKey);
    try {
      updateTab(tabKey, { progress: 10, stage: "📤 Uploading PDF..." });
      addLog(tabKey, "Uploading bank statement PDF...");
      updateTab(tabKey, { progress: 30, stage: "🔍 Extracting transactions..." });
      addLog(tabKey, `Extracting with ${banks.find(b => b.slug === bank)?.label || bank}...`);

      const result = await extractBankStatement(
        file,
        bank,
        tabKey,
        selectedClient?.id || null,
      );

      // Flip extracting OFF and reveal the result the instant the extraction
      // call itself succeeds — don't make the download button wait on the
      // history refresh below. That refresh is a separate request and can
      // queue behind a still-running extraction on the OTHER tab; gating the
      // button on it was exactly why Loan showed "complete" in the logs but
      // stayed stuck on the progress bar while Current was still extracting.
      updateTab(tabKey, { progress: 100, stage: "✅ Extraction complete", extracting: false });
      saveResultToTab(tabKey, result);
      addLog(tabKey, `✅ ${result.transaction_count} transactions extracted`, "ok");
      if (result.period) addLog(tabKey, `📅 Period: ${result.period}`, "ok");
      endBankProcessing();

      // Fire-and-forget — refreshes the history list in the background.
      // Intentionally not awaited so it can never delay the button above.
      loadStatements(tabKey);
    } catch (err) {
      updateTab(tabKey, { extracting: false, apiError: err.message, stage: "❌ Failed" });
      addLog(tabKey, `❌ ${err.message}`, "err");
      endBankProcessing();
    }
  }

  // ── Download Excel ──────────────────────────────────────────────────────────
  async function handleDownload(stmtId, bankSlug, filenameHint) {
    const tabKey = bankSub;
    try {
      const bankLabel = [...CURRENT_BANKS, ...LOAN_BANKS].find(b => b.slug === bankSlug)?.label || bankSlug;
      const fallback = `BankStatement_${bankLabel}_${stmtId}.xlsx`;
      await downloadBankStatementExcel(stmtId, bankStatementFilename(filenameHint, fallback));
      addLog(tabKey, "📥 Excel downloaded", "ok");
    } catch (err) {
      addLog(tabKey, `❌ Download failed: ${err.message}`, "err");
    }
  }

  // ── Inject invoices ─────────────────────────────────────────────────────────
  async function handleInject(stmtId, bankSlug, filenameHint) {
    const tabKey = bankSub;
    const file = ts.invoicesFile;
    if (!file) return;
    updateTab(tabKey, { injecting: true });
    addLog(tabKey, "🔗 Injecting invoices into bank statement...");
    try {
      const bankLabel = [...CURRENT_BANKS, ...LOAN_BANKS].find(b => b.slug === bankSlug)?.label || bankSlug;
      const fallback = `BankStatement_WithInvoices_${bankLabel}_${stmtId}.xlsx`;
      await injectAndDownload(
        stmtId,
        file,
        bankStatementFilename(filenameHint, fallback, "_WithInvoices"),
      );
      addLog(tabKey, "✅ Merged Excel downloaded", "ok");
      updateTab(tabKey, { invoicesFile: null });
    } catch (err) {
      addLog(tabKey, `❌ Injection failed: ${err.message}`, "err");
    } finally {
      updateTab(tabKey, { injecting: false });
    }
  }

  // ── Delete statement ────────────────────────────────────────────────────────
  function handleDelete(stmtId) {
    const tabKey = bankSub;
    setConfirmModal({
      title: "Delete Bank Statement",
      body: "Permanently remove this bank statement extraction and its Excel file? This cannot be undone.",
      onConfirm: async () => {
        try {
          await deleteMyBankStatement(stmtId);
          if (extracted?.id === stmtId) clearResultForTab(tabKey);
          await loadStatements(tabKey);
          addLog(tabKey, "🗑 Statement deleted", "ok");
        } catch (err) {
          addLog(tabKey, `❌ ${err.message}`, "err");
        }
      },
    });
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const fmtDate = d => d ? new Date(d).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  }) : "—";

  // "Merge Statements" is its own dedicated tool, not another current/loan
  // extraction view — render it standalone and skip the rest of this
  // component's render entirely. Safe to early-return here: every hook
  // above is called unconditionally regardless of bankSub.
  if (bankSub === "merge") {
    return <MergeStatementsWorkflow clientId={propClientId} clientName={propClientName} />;
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24 }}>

      {/* Sub-tabs — centered */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 4, background: "var(--surface)",
                      borderRadius: 12, padding: 4,
                      border: "1px solid var(--border)" }}>
          {BANK_SUB_TABS.map(t => (
            <button key={t.id}
              onClick={() => onSubTabChange(t.id)}
              style={{
                padding: "8px 18px", borderRadius: 9, border: "none", cursor: "pointer",
                fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 600,
                background: bankSub === t.id
                  ? "linear-gradient(135deg,var(--ai-deep),var(--ai-blue))"
                  : "transparent",
                color: bankSub === t.id ? "#fff" : "var(--muted-light)",
                transition: "all 0.18s",
              }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* ── LEFT: Extract Panel ── */}
        <div style={{ background: "var(--panel)", border: "1px solid var(--border)",
                      borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)",
                        display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 700, fontSize: 13 }}>
              Extract {bankSub === "current" ? "Current Account" : "Loan Account"} Statement
            </span>
            {extracted && <span style={{ fontSize: 11, color: "var(--success)" }}>
              ✓ {extracted.transaction_count} transactions
            </span>}
          </div>

          <div style={{ padding: 20 }}>
            {/* Client selector */}
            <div style={{ marginBottom: 16, background: "rgba(59,158,255,0.04)",
                          border: "1px solid rgba(59,158,255,0.15)", borderRadius: 12, padding: "12px 14px" }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600,
                               color: "var(--ai-blue)", marginBottom: 6 }}>Client</label>
              <select
                value={selectedClient?.id || ""}
                onChange={e => {
                  const c = clientList.find(x => String(x.id) === e.target.value);
                  setSelectedClient(c ? { id: c.id, name: c.name } : null);
                }}
                style={{ width: "100%", background: "var(--surface)", color: "var(--text)",
                         border: "1px solid var(--border)", borderRadius: 8,
                         padding: "8px 10px", fontSize: 13, fontFamily: "'Outfit',sans-serif" }}>
                <option value="">— Select client (optional) —</option>
                {clientList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {selectedClient && (
                <div style={{ fontSize: 11, color: "var(--success)", marginTop: 6 }}>
                  ✓ Statement will be saved to <strong>{selectedClient.name}</strong>
                </div>
              )}
            </div>

            {/* Bank picker */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)",
                             textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
                Select Bank
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {banks.map(b => (
                  <button key={b.slug}
                    onClick={() => setTs({ selectedBank: b.slug })}
                    style={{
                      display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                      background: ts.selectedBank === b.slug
                        ? "rgba(59,158,255,0.12)" : "var(--surface)",
                      border: `1px solid ${ts.selectedBank === b.slug
                        ? "rgba(59,158,255,0.35)" : "var(--border)"}`,
                      borderRadius: 10, cursor: "pointer", transition: "all 0.18s",
                      fontFamily: "'Outfit',sans-serif", color: "var(--text)",
                      textAlign: "left",
                    }}>
                    <span style={{ fontSize: 20 }}>{b.icon}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{b.label}</div>
                      {ts.selectedBank === b.slug && (
                        <div style={{ fontSize: 11, color: "var(--ai-blue)", marginTop: 2 }}>
                          ✓ Selected
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* ── Result panel — shown whenever extracted is set, NO selectedBank gate ── */}
            {extracted && !ts.extracting ? (
              <div style={{ textAlign: "center", padding: "12px 0 8px" }}>
                <div style={{ fontSize: 32, marginBottom: 6 }}>✅</div>
                <div style={{ fontWeight: 700, color: "var(--success)", marginBottom: 2 }}>
                  Extraction Complete
                </div>
                <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
                  {extracted.transaction_count} transactions
                  {extracted.period ? ` · ${extracted.period}` : ""}
                </div>

                {/* Download button */}
                <button className="btn btn-green" style={{ marginBottom: 8 }}
                  onClick={() => handleDownload(extracted.id, extracted.bank, extracted.filename_hint)}>
                  📥 Download {bankSub === "loan" ? "Loan" : "Bank"} Statement Excel
                </button>

                {/* Reset */}
                <button className="btn btn-ghost btn-sm"
                  style={{ width: "auto", marginTop: 4 }}
                  onClick={() => {
                    clearResult();
                    setTs({ pdfFile: null, selectedBank: null, logs: [] });
                  }}>
                  ↺ Extract Another
                </button>
              </div>

            ) : ts.extracting ? (
              /* ── Progress bar ── */
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
                <div style={{ fontWeight: 600, color: "var(--ai-blue)", marginBottom: 4 }}>
                  {ts.stage}
                </div>
                <div style={{ background: "rgba(59,158,255,0.08)",
                              borderRadius: 8, height: 6, overflow: "hidden", marginTop: 10 }}>
                  <div style={{ height: "100%", width: `${ts.progress}%`,
                                background: "linear-gradient(90deg,var(--ai-deep),var(--ai-blue))",
                                transition: "width 0.4s ease", borderRadius: 8 }} />
                </div>
              </div>

            ) : ts.selectedBank ? (
              /* ── Upload zone — only when bank selected and no result yet ── */
              <>
                <div
                  className={`upload-zone ${ts.dragOver ? "drag-over" : ""}`}
                  onDrop={handleDrop}
                  onDragOver={e => { e.preventDefault(); setTs({ dragOver: true }); }}
                  onDragLeave={() => setTs({ dragOver: false })}
                  onClick={() => pdfRef.current?.click()}
                  style={{ marginBottom: ts.pdfFile ? 12 : 0 }}>
                  <div className="upload-icon">📄</div>
                  <div className="upload-title">Drop bank statement PDF here</div>
                  <div className="upload-sub">Click to browse</div>
                </div>
                <input ref={pdfRef} type="file" accept=".pdf"
                  style={{ display: "none" }} onChange={handlePdfSelect} />

                {ts.pdfFile && (
                  <div style={{ marginTop: 10 }}>
                    <div className="file-item">
                      <span className="file-name">📎 {ts.pdfFile.name}</span>
                      <span className="file-size">{(ts.pdfFile.size / 1024).toFixed(0)} KB</span>
                      <button className="file-remove"
                        onClick={() => setTs({ pdfFile: null })}>✕</button>
                    </div>
                    <button className="btn btn-blue" style={{ marginTop: 8 }}
                      onClick={handleExtract}>
                      🚀 Extract Statement
                    </button>
                  </div>
                )}
              </>
            ) : null}

            {/* Log box */}
            {ts.logs.length > 0 && (
              <div ref={logRef} className="log-box" style={{ marginTop: 12 }}>
                {ts.logs.map((l, i) => (
                  <div key={i} className={l.type ? `log-${l.type}` : ""}>{">"} {l.msg}</div>
                ))}
              </div>
            )}
            {ts.apiError && (
              <div style={{ marginTop: 8, fontSize: 12, color: "var(--error)" }}>
                ⚠ {ts.apiError}
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Inject Invoices Panel ── */}
        <div style={{ background: "var(--panel)", border: "1px solid var(--border)",
                      borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)" }}>
            <span style={{ fontWeight: 700, fontSize: 13 }}>Inject Invoices</span>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
              Match and insert invoices into an extracted bank statement
            </div>
          </div>

          <div style={{ padding: 20 }}>
            {/* Step guide */}
            <div style={{ marginBottom: 20 }}>
              {[
                { n: 1, text: "Extract a bank statement (left panel) or select one from history below" },
                { n: 2, text: "Optionally download the Excel to verify / correct it" },
                { n: 3, text: "Upload the client's VAT Purchases Excel to inject" },
              ].map(s => (
                <div key={s.n} style={{ display: "flex", gap: 10, alignItems: "flex-start",
                                        marginBottom: 10 }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                                 background: "rgba(59,158,255,0.15)",
                                 border: "1px solid rgba(59,158,255,0.3)",
                                 display: "flex", alignItems: "center", justifyContent: "center",
                                 fontSize: 10, fontWeight: 700, color: "var(--ai-blue)" }}>
                    {s.n}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--muted-light)", paddingTop: 2,
                                 lineHeight: 1.5 }}>{s.text}</div>
                </div>
              ))}
            </div>

            {/* Active extraction target */}
            {extracted ? (
              <div style={{ background: "rgba(34,211,168,0.07)",
                             border: "1px solid rgba(34,211,168,0.2)",
                             borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--success)",
                               marginBottom: 4 }}>✓ Active extraction target</div>
                <div style={{ fontSize: 12, color: "var(--text)", fontWeight: 600 }}>
                  {[...CURRENT_BANKS, ...LOAN_BANKS].find(b => b.slug === extracted.bank)?.label || extracted.bank}
                </div>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>
                  {extracted.transaction_count} transactions · {fmtDate(extracted.created_at)}
                </div>
              </div>
            ) : (
              <div style={{ background: "rgba(59,158,255,0.04)",
                             border: "1px dashed rgba(59,158,255,0.2)",
                             borderRadius: 10, padding: "10px 14px", marginBottom: 14,
                             fontSize: 12, color: "var(--muted)", textAlign: "center" }}>
                No extraction selected. Extract one on the left or pick from history below.
              </div>
            )}

            {/* Upload invoices Excel */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)",
                             textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
                Upload Invoices Excel
              </div>
              {ts.invoicesFile ? (
                <div className="file-item">
                  <span className="file-name">📊 {ts.invoicesFile.name}</span>
                  <span className="file-size">{(ts.invoicesFile.size / 1024).toFixed(0)} KB</span>
                  <button className="file-remove"
                    onClick={() => setTs({ invoicesFile: null })}>✕</button>
                </div>
              ) : (
                <button className="btn btn-ghost"
                  onClick={() => invRef.current?.click()}
                  style={{ fontSize: 13 }}>
                  📂 Browse for Invoices Excel
                </button>
              )}
              <input ref={invRef} type="file" accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={e => setTs({ invoicesFile: e.target.files?.[0] || null })} />
            </div>

            {/* Inject button */}
            {extracted && ts.invoicesFile && (
              (extracted.bank === "nedbank_current" || extracted.bank === "standard_bank_loan" || extracted.bank === "sbs_loan") ? (
                <div style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)",
                               borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "var(--warning)" }}>
                  ⚠ Invoice injection is not available for{" "}
                  {extracted.bank === "standard_bank_loan"
                    ? "Standard Bank Finance Lease"
                    : extracted.bank === "sbs_loan"
                    ? "Swaziland Building Society (Loan)"
                    : "Nedbank (Current)"}{" "}
                  statements. Download the extracted Excel above to work with it directly.
                </div>
              ) : (
              <button className="btn btn-purple"
                disabled={ts.injecting}
                onClick={() => handleInject(extracted.id, extracted.bank, extracted.filename_hint)}>
                {ts.injecting ? "⏳ Injecting..." : "🔗 Inject & Download Merged Excel"}
              </button>
              )
            )}
          </div>
        </div>
      </div>

      {/* ── History ── */}
      <div style={{ background: "var(--panel)", border: "1px solid var(--border)",
                    borderRadius: 16, overflow: "hidden", marginTop: 16 }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)",
                      display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>
            Extraction History — {bankSub === "current" ? "Current Account" : "Loan Account"}
          </span>
          <button onClick={() => loadStatements()}
            style={{ background: "transparent", border: "none", color: "var(--ai-blue)",
                     cursor: "pointer", fontSize: 12, fontFamily: "'Outfit',sans-serif" }}>
            ↻ Refresh
          </button>
        </div>
        <div style={{ padding: 20 }}>
          {listLoading ? (
            <div style={{ textAlign: "center", color: "var(--muted)", fontSize: 13, padding: "20px 0" }}>
              Loading...
            </div>
          ) : stmtList.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{ fontSize: 32, opacity: 0.15, marginBottom: 8 }}>🏦</div>
              <div style={{ fontSize: 13, color: "var(--muted)" }}>
                No extractions yet. Upload a PDF on the left to get started.
              </div>
            </div>
          ) : (
            <div>
              {stmtList.map(stmt => {
                const bankDef = [...CURRENT_BANKS, ...LOAN_BANKS].find(b => b.slug === stmt.bank);
                const isActive = extracted?.id === stmt.id;
                return (
                  <div key={stmt.id}
                    style={{
                      display: "flex", gap: 14, alignItems: "center",
                      background: isActive ? "rgba(34,211,168,0.06)" : "var(--surface)",
                      border: `1px solid ${isActive ? "rgba(34,211,168,0.2)" : "var(--border)"}`,
                      borderRadius: 12, padding: "12px 16px", marginBottom: 10,
                      transition: "border-color 0.2s",
                    }}>
                    {/* Icon */}
                    <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                                   background: "rgba(59,158,255,0.1)",
                                   border: "1px solid rgba(59,158,255,0.15)",
                                   display: "flex", alignItems: "center",
                                   justifyContent: "center", fontSize: 18 }}>
                      {bankDef?.icon || "🏦"}
                    </div>

                    {/* Meta */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>
                        {stmt.bank_display || bankDef?.label || stmt.bank}
                        {isActive && (
                          <span style={{ marginLeft: 8, fontSize: 10, color: "var(--success)",
                                          background: "rgba(34,211,168,0.1)",
                                          padding: "2px 8px", borderRadius: 20 }}>
                            Active
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>
                        {stmt.transaction_count} transactions
                        {stmt.period ? ` · ${stmt.period}` : ""}
                        {" · "}{fmtDate(stmt.created_at)}
                      </div>
                      {stmt.filename_hint && (
                        <div style={{ fontSize: 11, color: "var(--muted)", fontStyle: "italic" }}>
                          {stmt.filename_hint}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
                      {/* Use for injection */}
                      {!isActive && (
                        <button
                          onClick={() => saveResult(stmt)}
                          style={{
                            background: "rgba(59,158,255,0.1)", color: "var(--ai-blue)",
                            border: "1px solid rgba(59,158,255,0.25)", borderRadius: 9,
                            padding: "7px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                            fontFamily: "'Outfit',sans-serif",
                          }}>
                          Use
                        </button>
                      )}
                      {/* Download */}
                      {stmt.excel_exists !== false && (
                        <button
                          onClick={() => handleDownload(stmt.id, stmt.bank, stmt.filename_hint)}
                          style={{
                            background: "rgba(34,211,168,0.1)", color: "var(--success)",
                            border: "1px solid rgba(34,211,168,0.2)", borderRadius: 9,
                            padding: "7px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                            fontFamily: "'Outfit',sans-serif",
                          }}>
                          📥 Download
                        </button>
                      )}
                      {/* Delete */}
                      <button
                        onClick={() => handleDelete(stmt.id)}
                        style={{
                          background: "rgba(239,68,68,0.08)", color: "var(--error)",
                          border: "1px solid rgba(239,68,68,0.2)", borderRadius: 9,
                          padding: "7px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                          fontFamily: "'Outfit',sans-serif",
                        }}>
                        🗑
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ConfirmModal state={confirmModal} onClose={() => setConfirmModal(null)} />
    </div>
  );
}
